import { useCallback, useEffect, useRef, useState } from 'react'
import {
  BLOCOS_DA_LISTA,
  ROTULO_DO_BLOCO,
  blocoDaConversa,
  corDoTextoSobre,
  semAssinatura,
  souApoioNaConversa,
  type BlocoDaLista,
  type ConversaDto,
} from '@crm/shared'
import { Avatar } from '../../componentes/Avatar'
import { IconeApoio, IconeBuscar } from '../../componentes/icones'

/** As cores dos blocos, as da prévia aprovada em 05/10/2026. */
const COR_DO_BLOCO: Record<BlocoDaLista, string> = {
  solicitacao: 'bg-amber-100 text-amber-900',
  'saiu-do-apoio': 'bg-violet-100 text-violet-900',
  'nao-respondidas': 'bg-emerald-100 text-emerald-900',
  'com-apoio': 'bg-tinta-100 text-tinta-600',
  retorno: 'bg-blue-100 text-blue-900',
  conversas: 'bg-tinta-100 text-tinta-600',
}
import { NomeDoContato } from '../../componentes/NomeDoContato'
import { Etiqueta, juntar } from '../../componentes/ui'
import { formatarDataHora, horarioDaLista } from '../../formato'

/**
 * A lista de conversas (5.1). Aqui está a correção da dor principal.
 *
 * Não ordena por mensagem mais recente: agrupa por estado. "Aguardando
 * resposta" fica no topo e **não afunda** quando outra conversa recebe
 * mensagem, que é exatamente o que fazia a equipe usar "marcar como não lida"
 * como fila de pendência improvisada no Digisac.
 */

function tempoDeEspera(segundos: number | null): string {
  if (segundos === null) return ''
  if (segundos < 60) return `${segundos}s`
  const minutos = Math.floor(segundos / 60)
  if (minutos < 60) return `${minutos}min`
  const horas = Math.floor(minutos / 60)
  if (horas < 24) return `${horas}h${String(minutos % 60).padStart(2, '0')}`
  return `${Math.floor(horas / 24)}d`
}

/**
 * Quem escreveu a última mensagem que saiu (mapa de uso, 08/10/2026). Era
 * "você:" para toda mensagem da empresa, e com equipe o vendedor lia "você"
 * no que o colega escreveu. Sem autor no CRM (celular, robô), é "você:" para o
 * dono da conversa e "empresa:" para os outros.
 */
function quemEscreveu(conversa: ConversaDto, euId: string | undefined): string {
  if (conversa.previaDirecao !== 'saida') return ''
  // Sem autor no CRM costuma ser o dono respondendo pelo celular: para ele, "você".
  if (!conversa.previaAutorId) return conversa.responsavelId === euId ? 'você: ' : 'empresa: '
  if (conversa.previaAutorId === euId) return 'você: '
  return `${(conversa.previaAutorNome ?? 'equipe').split(' ')[0]}: `
}

function previaDoTipo(conversa: ConversaDto): string {
  // Sem a assinatura: numa linha só, "você: *Mike*" não diz nada que o
  // "você:" já não tenha dito, e empurra a frase para fora.
  if (conversa.previa) return semAssinatura(conversa.previa)
  switch (conversa.previaTipo) {
    case 'imagem':
      return 'imagem'
    case 'audio':
      return 'áudio'
    case 'video':
      return 'vídeo'
    case 'documento':
      return 'documento'
    case 'nota_interna':
      return 'comentário'
    default:
      return 'sem mensagem'
  }
}

export function ListaConversas({
  conversas,
  selecionada,
  aoSelecionar,
  aoAlternarLeitura,
  listaMisturada = false,
  escopo,
  usuarioId,
}: {
  conversas: ConversaDto[]
  selecionada: string | null
  aoSelecionar: (conversa: ConversaDto) => void
  aoAlternarLeitura: (conversa: ConversaDto, naoLida: boolean) => void
  /**
   * Aberto e encerrado na mesma lista. Só então o cartão precisa dizer qual é
   * qual — nas listas de um status só, o selo repetido em toda linha não
   * informa nada e ainda rouba espaço das etiquetas que informam.
   */
  listaMisturada?: boolean
  /** O recorte aberto, para o cartão não repetir o que a aba já disse. */
  escopo: 'todas' | 'minhas' | 'fila'
  /** Quem está olhando: os blocos do apoio dependem de quem é o dono e quem é o apoio. */
  usuarioId: string
}) {
  /**
   * Segura o cartão no lugar por um instante depois de marcar.
   *
   * Sem isso o cartão pula de bloco no mesmo quadro do clique, e o que estava
   * embaixo do ponteiro vira outra conversa — clicar duas vezes seguidas
   * abria a conversa errada. Com a pausa, a cor esvai de verde para cinza no
   * lugar e só então ele desce, o que também deixa ver o que aconteceu.
   */
  const [segurando, setSegurando] = useState<{
    id: string
    naoLidaAntes: boolean
    naoLidaDepois: boolean
    /** Fase 1: ainda pintado como antes, para a transição ter de onde sair. */
    corAntiga: boolean
  } | null>(null)
  const relogios = useRef<ReturnType<typeof setTimeout>[]>([])
  const quadro = useRef<number | null>(null)

  useEffect(
    () => () => {
      for (const r of relogios.current) clearTimeout(r)
      if (quadro.current !== null) cancelAnimationFrame(quadro.current)
    },
    [],
  )

  /**
   * Duas fases, porque a transição de CSS não sobrevive à troca de classe
   * feita no mesmo quadro em que o dado muda: o navegador já pinta a cor
   * final e não há de onde animar.
   *
   * Fase 1 (imediata): o cartão continua pintado como estava e parado no
   * lugar. Fase 2 (quadro seguinte): a cor passa para a nova, e aí sim a
   * transição roda sobre um elemento que já estava montado e pintado.
   * Terminada a transição, a pausa acaba e o cartão desce.
   *
   * A cor da fase 2 vem do valor que acabou de ser pedido, não da resposta do
   * servidor. Esperando a resposta, o começo do fade dependia da rede: numa
   * volta mais lenta que a pausa, a cor só mudava depois de o cartão já ter
   * descido, e não se via fade nenhum.
   */
  const alternar = useCallback(
    (conversa: ConversaDto, naoLida: boolean) => {
      for (const r of relogios.current) clearTimeout(r)
      relogios.current = []
      if (quadro.current !== null) cancelAnimationFrame(quadro.current)

      setSegurando({
        id: conversa.chamadoId,
        naoLidaAntes: conversa.naoLida,
        naoLidaDepois: naoLida,
        corAntiga: true,
      })

      /*
        A fase 2 espera o quadro seguinte, não um relógio.

        `requestAnimationFrame` aninhado é o jeito de dizer "depois que o
        navegador pintou a fase 1": só a partir daí a troca de cor tem de onde
        animar. Um `setTimeout` curto parece equivalente e não é — em aba sem
        foco o navegador junta os temporizadores curtos num só disparo, e as
        duas fases aconteceriam no mesmo instante.
      */
      quadro.current = requestAnimationFrame(() => {
        quadro.current = requestAnimationFrame(() => {
          setSegurando((a) => (a ? { ...a, corAntiga: false } : a))
        })
      })

      // Um pouco mais que a transição, para o movimento vir depois dela.
      relogios.current.push(setTimeout(() => setSegurando(null), 700))

      aoAlternarLeitura(conversa, naoLida)
    },
    [aoAlternarLeitura],
  )

  /** Onde o cartão fica agora: no lugar antigo enquanto durar a pausa. */
  const posicaoDe = (conversa: ConversaDto): boolean =>
    segurando?.id === conversa.chamadoId ? segurando.naoLidaAntes : conversa.naoLida

  /** Como o cartão está pintado agora. */
  const corDe = (conversa: ConversaDto): boolean => {
    if (segurando?.id !== conversa.chamadoId) return conversa.naoLida
    return segurando.corAntiga ? segurando.naoLidaAntes : segurando.naoLidaDepois
  }
  /**
   * Não respondidas primeiro, como um grupo só, e o resto na ordem da
   * regra: pendente, retorno, o resto (ADR 0004).
   *
   * Agrupar as não respondidas à parte é o que faz "sempre no topo" ser
   * verdade na tela. Mantendo só os grupos por estado, uma conversa sem
   * resposta que não está aguardando cairia lá para baixo, embaixo das já
   * respondidas — e a marcação deixaria de servir para voltar nela.
   *
   * O servidor já devolve ordenado; aqui só se separa em blocos.
   */
  /*
    Os blocos da lista (ADR 0043), na ordem do pedido de 05/10/2026:
    Solicitação de apoio, Saiu do apoio, Não respondidas, Com apoio, Retorno
    agendado e Conversas. A regra de cada conversa mora em `blocoDaConversa`
    (shared, com teste); aqui só se separa, mantendo a ordem do servidor
    dentro de cada bloco (ADR 0004).
  */
  // [INÍCIO] Fila da mesa da escola (ADR 0082)
  const grupos =
    escopo === 'fila'
      ? [
          {
            chave: 'fila',
            rotulo: 'Na ordem de chegada',
            cor: 'bg-amber-100 text-amber-900',
            itens: conversas,
          },
        ]
      : BLOCOS_DA_LISTA.map((bloco) => ({
          chave: bloco,
          rotulo: ROTULO_DO_BLOCO[bloco],
          cor: COR_DO_BLOCO[bloco],
          itens: conversas.filter((c) => blocoDaConversa(c, usuarioId, posicaoDe(c)) === bloco),
        })).filter((g) => g.itens.length > 0)
  // [FIM] Fila da mesa da escola (ADR 0082)

  if (conversas.length === 0) {
    return (
      <p className="p-6 text-center text-sm text-tinta-400">Nenhuma conversa por aqui ainda.</p>
    )
  }

  return (
    <div>
      {grupos.map((grupo) => (
        <section key={grupo.chave}>
          <header
            className={juntar(
              'sticky top-0 z-20 flex items-center justify-between px-3 py-1.5 text-xs font-semibold uppercase',
              grupo.cor,
            )}
          >
            <span className="flex items-center gap-1">
              {grupo.chave === 'com-apoio' ? <IconeApoio className="h-3.5 w-3.5" /> : null}
              {grupo.rotulo}
            </span>
            <span>{grupo.itens.length}</span>
          </header>

          <ul>
            {grupo.itens.map((conversa) => (
              <li
                key={conversa.chamadoId}
                data-chamado={conversa.chamadoId}
                className={juntar(
                  'group relative border-b border-tinta-100 transition-colors duration-500',
                  // Conversa minha com apoio aceito não fica verde: a vez é do suporte (ADR 0043).
                  corDe(conversa) &&
                    !(conversa.responsavelId === usuarioId && conversa.apoios.length > 0)
                    ? 'bg-naolida hover:bg-naolida-hover'
                    : 'bg-lida hover:bg-lida-hover',
                  /*
                    A conversa aberta é marcada por uma faixa na lateral, não
                    por outra cor de fundo: trocar o fundo apagaria o estado de
                    leitura justamente na conversa que se está olhando.
                  */
                  selecionada === conversa.chamadoId &&
                    'before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-marca',
                )}
              >
                {/*
                  Marcador de respondida/não respondida, logo abaixo do
                  tempo de espera.

                  Fica fora do botão de propósito: caixa de seleção dentro de
                  botão é HTML inválido, e o clique acabaria abrindo a conversa
                  em vez de marcar. Por isso ele é irmão do botão, posicionado
                  por cima do canto onde o espaço já está reservado.

                  Aparece só com o ponteiro em cima. Sem ele, o mesmo canto
                  mostra a bolinha de sem resposta: o estado continua
                  visível, e a ação aparece quando dá para clicar nela.
                */}
                <label
                  className="absolute right-3 top-8 z-10 hidden cursor-pointer select-none items-center gap-1 rounded bg-superficie px-1 py-0.5 text-[11px] text-tinta-600 shadow-suave ring-1 ring-tinta-200 group-hover:flex"
                  title={
                    conversa.naoLida
                      ? 'Marque como respondida quando não há o que responder (o cliente mandou um "ok"): sai das pendências e para o aviso de sem resposta.'
                      : 'Desmarque para voltar a ela depois, como não respondida.'
                  }
                >
                  {/*
                    A caixa marca "respondida" e o texto acompanha o estado
                    (mapa de uso, 08/10/2026). Antes dizia "não respondida"
                    nos dois casos, só com a caixa mudando.
                  */}
                  <input
                    type="checkbox"
                    checked={!conversa.naoLida}
                    onChange={(e) => alternar(conversa, !e.target.checked)}
                    className="h-3 w-3 cursor-pointer accent-marca"
                  />
                  {conversa.naoLida ? 'não respondida' : 'respondida'}
                </label>

                <button
                  type="button"
                  onClick={() => {
                    /*
                      Abrir não tira da fila. Quem tira é a resposta.

                      Antes o clique já baixava o cartão para "Conversas", e
                      bastava entrar para ver o que o cliente escreveu, sair
                      para pensar e a conversa sumia da fila de pendência sem
                      ninguém ter respondido nada. Por isso o rótulo deixou
                      de ser "não lida": a conversa só sai do bloco quando sai
                      uma mensagem, e o marcador continua disponível para quem
                      quiser resolver na mão.
                    */
                    aoSelecionar(conversa)
                  }}
                  className="flex w-full items-start gap-2.5 px-3 py-2 text-left"
                  data-abrir-conversa
                >
                  {/*
                    A foto do WhatsApp, do mesmo jeito que aparece no celular.

                    O cartão só tinha texto, e numa lista de quarenta conversas
                    o olho lê linha por linha para achar a certa. A foto é
                    reconhecida antes do nome — e quem não tem foto continua
                    identificável pelas iniciais sobre uma cor fixa, que é o
                    caso da maioria.
                  */}
                  <Avatar
                    contatoId={conversa.contatoId}
                    nome={conversa.nome}
                    cinza={!corDe(conversa)}
                  />

                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <span
                        className={juntar(
                          'min-w-0 text-sm',
                          conversa.naoLida ? 'font-semibold text-tinta-900' : 'text-tinta-800',
                        )}
                      >
                        {/* [INÍCIO] Fila da mesa da escola (ADR 0082) */}
                        {escopo === 'fila' && conversa.posicaoFila != null ? (
                          <span
                            className="mr-1.5 inline-block shrink-0 rounded bg-amber-100 px-1.5 text-[11px] font-semibold tabular-nums text-amber-800"
                            title={`Posição ${conversa.posicaoFila} na fila`}
                          >
                            {conversa.posicaoFila}º
                          </span>
                        ) : null}
                        {/* [FIM] Fila da mesa da escola (ADR 0082) */}
                        {/*
                        O nome do WhatsApp também aqui: é neste cartão que se
                        procura a conversa, e quem lembra do cliente pelo nome
                        que ele usa no WhatsApp não acharia o renomeado.
                      */}
                        <NomeDoContato nome={conversa.nome} nomeOriginal={conversa.nomeOriginal} />
                      </span>

                      {/*
                      O carimbo é a hora da última mensagem, como no WhatsApp.

                      Antes era o cronômetro de espera — "5min", "1d" — e o
                      número parecia a hora sem ser: quem batia o olho lia
                      "1d" e não sabia se o cliente escreveu de manhã ou de
                      madrugada. A urgência continua aqui, na cor do carimbo e
                      no tempo exato ao passar o mouse; o que se lê de longe
                      passa a ser quando foi.
                    */}
                      {conversa.ultimaMensagemEm ? (
                        <span
                          className={juntar(
                            'shrink-0 rounded px-1 text-xs font-medium tabular-nums',
                            conversa.esperandoSegundos === null
                              ? 'text-tinta-600'
                              : conversa.cronometroEstourado
                                ? 'bg-red-100 text-red-700'
                                : 'bg-amber-100 text-amber-800',
                          )}
                          title={
                            conversa.esperandoSegundos === null
                              ? `Última mensagem em ${formatarDataHora(conversa.ultimaMensagemEm)}`
                              : `Última mensagem em ${formatarDataHora(conversa.ultimaMensagemEm)}, ${tempoDeEspera(conversa.esperandoSegundos)} sem resposta`
                          }
                        >
                          {horarioDaLista(conversa.ultimaMensagemEm)}
                        </span>
                      ) : null}
                    </div>

                    <div className="flex items-center justify-between gap-2">
                      <span
                        className={juntar(
                          'truncate text-xs',
                          /*
                          `tinta-400` reprovava em contraste nos dois fundos
                          (2,7:1) — vinha de antes, e com o fundo verde piorava.
                          `tinta-600` passa nos dois (6,9:1 e 6,0:1) e a
                          hierarquia continua vindo do peso do nome.
                        */
                          'text-tinta-600',
                        )}
                      >
                        {quemEscreveu(conversa, usuarioId)}
                        {previaDoTipo(conversa)}
                      </span>
                      <div className="flex shrink-0 items-center gap-1">
                        {conversa.temAgendamento ? <span title="Mensagem agendada">⏰</span> : null}
                        {/* Some no hover porque o marcador ocupa este mesmo canto. */}
                        {conversa.naoLida ? (
                          <span
                            className="h-2 w-2 rounded-full bg-marca group-hover:invisible"
                            title="Sem resposta"
                          />
                        ) : null}
                      </div>
                    </div>

                    {/*
                      O trecho achado por "Buscar em mensagens".

                      Sem ele, a lista só dizia que a palavra estava em algum
                      lugar daquela conversa, e achar onde era rolar o
                      histórico inteiro. Aqui aparece a frase, e o clique no
                      cartão abre a conversa já nela.
                    */}
                    {conversa.encontrada ? (
                      <div className="mt-1 flex items-start gap-1.5 rounded-md bg-amber-50 px-2 py-1 ring-1 ring-amber-200">
                        <IconeBuscar className="mt-0.5 h-3 w-3 shrink-0 text-amber-700" />
                        <span className="min-w-0 flex-1">
                          <span className="block text-[11px] leading-snug text-tinta-700">
                            {conversa.encontrada.trecho}
                          </span>
                          <span className="block text-[10px] text-amber-700">
                            {formatarDataHora(conversa.encontrada.enviadaEm)} · clique para abrir
                            aqui
                          </span>
                        </span>
                      </div>
                    ) : null}

                    {conversa.responsavelId === usuarioId && conversa.saiuDoApoio?.recado ? (
                      <p className="mt-0.5 truncate text-[11px] text-violet-900">
                        {conversa.saiuDoApoio.porNome}: {conversa.saiuDoApoio.recado}
                      </p>
                    ) : null}

                    <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                      {/*
                        "na fila" só quando informa.

                        Na aba Fila é o que todas as linhas são, e repetir a
                        etiqueta quarenta vezes rouba a linha da prévia sem
                        dizer nada. Em conversa encerrada também não diz: fila
                        é estado de quem ainda espera atendimento.
                      */}
                      {conversa.responsavelNome ? (
                        <span className="truncate text-[11px] text-tinta-400">
                          {conversa.responsavelNome}
                        </span>
                      ) : escopo !== 'fila' && conversa.status !== 'fechado' ? (
                        <Etiqueta cor="ambar">na fila</Etiqueta>
                      ) : null}

                      {/* As etiquetas do apoio (ADR 0043): quem pediu, quem ajuda, para quem. */}
                      {conversa.pedidoDeApoioParaMim ? (
                        <Etiqueta cor="ambar">
                          pedido de {conversa.pedidoDeApoioParaMim.deNome}
                        </Etiqueta>
                      ) : null}
                      {souApoioNaConversa(conversa, usuarioId) ? (
                        <Etiqueta cor="verde">apoio para {conversa.responsavelNome}</Etiqueta>
                      ) : null}
                      {conversa.responsavelId === usuarioId && conversa.apoios.length > 0 ? (
                        <Etiqueta cor="neutra">
                          com {conversa.apoios.map((a) => a.nome).join(', ')}
                        </Etiqueta>
                      ) : null}
                      {conversa.responsavelId === usuarioId &&
                      conversa.pedidosDeApoio.length > 0 ? (
                        <Etiqueta cor="neutra">
                          apoio pedido a {conversa.pedidosDeApoio.map((a) => a.nome).join(', ')}
                        </Etiqueta>
                      ) : null}

                      {listaMisturada && conversa.status === 'fechado' ? (
                        <Etiqueta cor="neutra">encerrado</Etiqueta>
                      ) : null}

                      {conversa.noCrm && conversa.etapaNome ? (
                        <span
                          className="truncate text-[11px] text-tinta-400"
                          title={
                            conversa.funilNome
                              ? `${conversa.funilNome} · ${conversa.etapaNome}`
                              : conversa.etapaNome
                          }
                        >
                          {conversa.etapaNome}
                        </span>
                      ) : null}
                    </div>

                    {/*
                      Etiquetas, na linha de baixo, da esquerda para a direita:
                      começam alinhadas com o nome e a prévia, e a leitura segue
                      a mesma direção do resto do cartão (pedido do Mike; antes
                      ficavam encostadas na direita).
                    */}
                    {conversa.tags.length > 0 ? (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {conversa.tags.map((tag) => (
                          <span
                            key={tag.id}
                            className="rounded px-1.5 py-0.5 text-[10px] font-medium"
                            style={{ backgroundColor: tag.cor, color: corDoTextoSobre(tag.cor) }}
                            title={tag.nome}
                          >
                            {tag.nome}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}
